<template>
  <form @submit.prevent="submit" :class="errorClass">
    <rp-multiselect
      v-model="placeId"
      :options="hospitals"
      label="description"
      keyProp="place_id"
      placeholder="Google Search for Hospital / Clinic"
      @input="setHospital"
      @click.native="onClickSearch"
      @search-change="searchChange"
      searchable
      closeOnSelect
      material />

    <div v-if="showTip" class="add-hospital-tip">
      <span>Can't find your hospital with search? You can manually enter the hospital details below to add it to the system.</span>
      <rp-sprite class="close-button" name="close" @click.native="onCloseTip" />
    </div>

    <rp-textinput
      v-model="resource.name"
      placeholder="Name"
      :error="nameError"
      isRequired
      @input="touch('name')" />

    <rp-select
      v-model="resource.country"
      placeholder="Country"
      @input="touch('country')"
      :error="countryError"
      isRequired>
      
      <option v-for="country in countries" :key="country.name" :value="country">{{country.name}}</option>
    </rp-select>

    <rp-textinput
      v-model="resource.address_1"
      placeholder="Address Line 1"
      :error="address_1Error"
      isRequired
      @input="touch('address_1')" />

    <rp-textinput
      v-model="resource.address_2"
      placeholder="Address Line 2"
      @input="touch('address_2')" />

    <rp-textinput
      v-model="resource.town"
      placeholder="City"
      :error="townError"
      isRequired
      @input="touch('town')" />

    <rp-textinput
      v-model="resource.county"
      placeholder="State / Province / Region"
      :error="countyError"
      isRequired />

    <rp-textinput
      v-model="resource.postcode"
      placeholder="ZIP / Postcode"
      :error="postcodeError"
      isRequired
      @input="touch('postcode')" />

    <rp-textinput
      v-if="isAdmin || 
      (!isAdmin && resource.latitude && resource.latitude != '' && resource.longitude && resource.longitude != '')"
      :disabled="!isAdmin"
      v-model="resource.latitude"
      placeholder="Latitude"
      @input="touch('latitude')" />

    <rp-textinput
      v-if="isAdmin || 
      (!isAdmin && resource.latitude && resource.latitude != '' && resource.longitude && resource.longitude != '')"
      :disabled="!isAdmin"
      v-model="resource.longitude"
      placeholder="Longitude"
      @input="touch('longitude')" />

    <rp-checkbox
      v-model="isAddImage"
      :label="addImageLabel" />

    <rp-imagecrop
      v-if="isAddImage" 
      v-model="resource.logo" />

    <rp-checkbox
      v-if="!editForm"
      v-model="resource.assign_hospital"
      label="Associate myself with this hospital"
      :error="assign_hospitalError"
      @input="touch('assign_hospital')" />

    <rp-button :disabled="isInvalid" type="primary">Submit</rp-button>
  </form>
</template>

<script>
  import {validationMixin} from 'vuelidate'
  import {email, required, requiredIf, sameAs} from 'vuelidate/lib/validators'
  import {errorMessages} from '../../../api/error_messages'

  import {getHospitalsByName, getHospitalByID} from '../../../api/google'

  import form from '../../../mixins/form'

  import Button from '../../../components/ui/inputs/Button'
  import Password from '../../../components/ui/inputs/PasswordInput'
  import TextInput from '../../../components/ui/inputs/TextInput'
  import DatePicker from '../../../components/ui/inputs/DatePicker'
  import Multiselect from '../../../components/ui/inputs/Multiselect'
  import Select from '../../../components/ui/inputs/StyledSelect'
  import Checkbox from '../../../components/ui/inputs/Checkbox'
  import ImageCropper from '../../../components/ui/ImageCropper'
  import Sprite from '../../../components/ui/Sprite'

  export default {
    name: 'HospitalForm',
    mixins: [validationMixin, form],

    data() {
      return {
        hospitals: [],
        timeout: null,
        placeId: '',
        isAddImage: false,
        showTip: false,
        didShowTip: false,
      }
    },

    props: {
      countries: Array,
      editForm: {default: false},
      isAdmin: {default: false}
    },

    components: {
      'rp-button': Button,
      'rp-password': Password,
      'rp-textinput': TextInput,
      'rp-datepicker': DatePicker,
      'rp-multiselect': Multiselect,
      'rp-select': Select,
      'rp-checkbox': Checkbox,
      'rp-imagecrop': ImageCropper,
      'rp-sprite': Sprite
    },

    watch: { 
      isAddImage: function(value) {
        if (!value) {
          this.$set(this.resource.logo, "dataUrl", null)
        }
      }
    },

    methods: {
      setHospital() {
        if (this.placeId) {
          getHospitalByID(this.placeId).then(({data}) => {
            data.country_id = data.country && data.country.id
            data.latitude = data.latlng[0]
            data.longitude = data.latlng[1]
            // Recreate logo props that are lost here
            data.logo = {dataUrl: null}
            this.resource = data;
          })
        } else {
          this.resetResource()
        } 
      },

      resetResource() {
        this.resource.name = ''
        this.resource.country = ''
        this.resource.address_1 = ''
        this.resource.address_2 = ''
        this.resource.town = ''
        this.resource.county = ''
        this.resource.postcode = ''
        this.resource.latitude = ''
        this.resource.longitude = ''
      },

      searchChange(hospitalName) {
        if(hospitalName.length >= 3) {
          // Debounce 1 sec
          if(this.timeout) {
            clearTimeout(this.timeout)
          }
          this.timeout = setTimeout(() => {
            getHospitalsByName(hospitalName).then(({data}) => {
              this.hospitals = data
            })
            this.timeout = null
          }, 1000)
        }
      },

      onClickSearch() {
        if (!this.didShowTip) {
          this.showTip = true
          this.didShowTip = true
        }
      },

      onCloseTip() {
        this.showTip = false
      },
    },

    computed: {
      ...errorMessages([
        'name',
        'country',
        'town',
        'address_1',
        'county',
        'postcode',
        'assign_hospital']),

      addImageLabel() {
        return (this.editForm && this.value.logo.sourceUrl && this.value.logo.sourceUrl !== '')
          ? 'Adjust hospital logo' : 'Add hospital logo'
      },
    },

    validations: {
      resource: {
        name: {required},
        country: {required},
        town: {required},
        address_1: {required},
        address_2: {required: false},
        county: {required},
        postcode: {required},
        latitude: {required: false},
        longitude: {required: false},
        assign_hospital: {required: false},
        logo: {
          required: false,
          dataUrl: {
            required: requiredIf(function() { return this.isAddImage })
          }
        }
      }
    }
  }
</script>

<style lang="sass" scoped>
  @import '../../../styles/base/variables'
  @import '../../../styles/helpers/mixins'

  .add-hospital-tip
    display: flex
    padding: 10px
    margin-top: 26px
    border: 1px solid $color-text
    border-radius: 5px
    text-align: center
    font-size: larger

    > span
      flex-grow: 1
      align-self: center

    .close-button
      width: 20px
      height: 20px
      cursor: pointer
      opacity: 0.3
</style>



// WEBPACK FOOTER //
// ../raypro_ui/src/components/ui/forms/Hospital.vue